"use client";

import { useState, type FormEvent } from "react";
import { Loader2, Search } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { JurisdictionStack } from "@/components/jurisdiction-stack";
import {
  resolveJurisdiction,
  SAMPLE_ADDRESSES,
  type ResolveResult,
} from "@/lib/jurisdiction";

export function JurisdictionLookup({
  initialAddress = "",
  showSamples = true,
  autoFocus = false,
}: {
  initialAddress?: string;
  showSamples?: boolean;
  autoFocus?: boolean;
}) {
  const [address, setAddress] = useState(initialAddress);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ResolveResult | null>(null);

  async function runLookup(value: string) {
    const trimmed = value.trim();
    if (trimmed.length < 3) {
      setError("Please enter a full street address.");
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const data = await resolveJurisdiction(trimmed);
      setResult(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong.");
    } finally {
      setLoading(false);
    }
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    void runLookup(address);
  }

  return (
    <div>
      <form onSubmit={handleSubmit} aria-label="Address lookup">
        <div className="flex flex-col gap-2.5 sm:flex-row">
          <Input
            inputSize="lg"
            placeholder="123 Main St, Cincinnati, OH 45202"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            leftIcon={<Search className="h-4 w-4" />}
            disabled={loading}
            autoFocus={autoFocus}
            autoComplete="street-address"
            aria-label="Street address"
          />
          <Button type="submit" size="lg" loading={loading} className="sm:w-44">
            {loading ? "Looking up…" : "Look up"}
          </Button>
        </div>

        {showSamples ? (
          <div className="mt-3 flex flex-wrap items-center gap-2 text-body-sm text-muted">
            <span>Try:</span>
            {SAMPLE_ADDRESSES.map((sample) => (
              <button
                key={sample}
                type="button"
                onClick={() => {
                  setAddress(sample);
                  void runLookup(sample);
                }}
                disabled={loading}
                className="cite rounded-[6px] border border-border bg-surface px-2 py-1 text-text transition-colors hover:border-primary/40 hover:text-primary disabled:opacity-50"
              >
                {sample}
              </button>
            ))}
          </div>
        ) : null}
      </form>

      {error ? (
        <div
          role="alert"
          className="mt-4 rounded-[8px] border border-warn/30 bg-warn/[0.06] px-4 py-3 text-body-sm text-warn"
        >
          {error}
        </div>
      ) : null}

      {loading && !result ? (
        <div className="mt-4 flex items-center justify-center gap-2.5 rounded-[10px] border border-border bg-surface px-4 py-10 text-muted">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-body-sm">Resolving jurisdictions…</span>
        </div>
      ) : null}

      {result ? <JurisdictionStack result={result} className="mt-4 animate-rise" /> : null}
    </div>
  );
}
